import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classNames from 'classnames';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';

import dibsCss from 'dibs-css';

import {
    deliveryReturnsTitle,
    deliveryTitle,
    methodNoteFreeShipping,
} from '../messages/pdpDetailsMessages.jsx';
import { PdpSharedDetailShipping } from '../PdpSharedDetails/PdpSharedDetailShipping.tsx';
import { PdpSharedDetailReturnPolicy } from '../PdpSharedDetails/PdpSharedDetailReturnPolicy.tsx';
import { PdpSharedBuyerProtectionBox } from '../PdpSharedBuyerProtectionBox/PdpSharedBuyerProtectionBox.tsx';
import { PdpRespDetail } from './PdpRespDetail.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';

import { type PdpRespShippingReturns_item$key } from './__generated__/PdpRespShippingReturns_item.graphql.ts';
import { type PdpRespShippingReturns_user$key } from './__generated__/PdpRespShippingReturns_user.graphql.ts';
import { type PdpRespShippingReturns_sku$key } from './__generated__/PdpRespShippingReturns_sku.graphql.ts';

import styles from './PdpRespDetails.scss';

const itemFragment = graphql`
    fragment PdpRespShippingReturns_item on Item {
        ...PdpSharedDetailReturnPolicy_item
        ...PdpSharedDetailShipping_item
        returnPolicy(userId: $userId) {
            buyerCopy {
                returnPolicySectionHeader {
                    htmlCopy
                }
            }
        }
        seller {
            isAccessible
        }
        isListingMigrated
    }
`;

const userFragment = graphql`
    fragment PdpRespShippingReturns_user on User {
        ...PdpSharedDetailShipping_user
        ...PdpSharedDetailReturnPolicy_user
    }
`;

const skuFragment = graphql`
    fragment PdpRespShippingReturns_sku on ItemSkuType {
        ...PdpSharedDetailReturnPolicy_sku
    }
`;

type Props = {
    currency: string;
    item: PdpRespShippingReturns_item$key;
    user?: PdpRespShippingReturns_user$key | null;
    sku?: PdpRespShippingReturns_sku$key | null;
    itemQuantity?: number | null;
    isCondensedTab?: boolean;
    isAccordionLayout?: boolean;
};

export const PdpRespShippingReturns: FC<Props> = ({
    currency,
    item: itemRef,
    sku: skuRef,
    user: userRef,
    itemQuantity,
    isCondensedTab,
    isAccordionLayout = false,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const sku = useFragment(skuFragment, skuRef);

    const { seller, isListingMigrated } = item;
    const { userId, itemId, afterDisplay } = useSbSelector(
        state => state.relayVariables?.variables
    );

    if (!seller?.isAccessible || isListingMigrated) {
        return null;
    }

    const returnPolicySectionHeader = (
        <span
            dangerouslySetInnerHTML={{
                __html: item?.returnPolicy?.buyerCopy?.returnPolicySectionHeader?.htmlCopy || '',
            }}
        />
    );

    const preLinkQuoteDetails = <>{methodNoteFreeShipping} </>;

    if (isAccordionLayout) {
        return (
            <div
                className={classNames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.gapSmall,
                    dibsCss.wFull
                )}
                data-tn="pdp-details-shipping"
            >
                <div
                    className={classNames(
                        dibsCss.flex,
                        dibsCss.flexRow,
                        dibsCss.wFull,
                        dibsCss.gapLarge
                    )}
                >
                    <div
                        className={classNames(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.gapXsmall,
                            dibsCss.flex1,
                            dibsCss.minW0
                        )}
                    >
                        <div
                            className={classNames(
                                dibsCss.textNoir,
                                dibsCss.sassyFontBodyLargeHeavy
                            )}
                            data-tn="pdp-spec-title"
                        >
                            {deliveryTitle}
                        </div>
                        <div
                            className={classNames(
                                dibsCss.sassyFontBody,
                                dibsCss.textNoir,
                                dibsCss.wFull
                            )}
                            data-tn="pdp-spec-shipping"
                        >
                            <PdpSharedDetailShipping
                                currency={currency}
                                isClient={!!afterDisplay}
                                item={item}
                                user={user}
                                preLinkQuoteDetails={preLinkQuoteDetails}
                                displayInline
                                itemQuantity={itemQuantity}
                            />
                        </div>
                    </div>
                    <div
                        className={classNames(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.gapXsmall,
                            dibsCss.flex1,
                            dibsCss.minW0
                        )}
                    >
                        <div
                            className={classNames(
                                dibsCss.textNoir,
                                dibsCss.sassyFontBodyLargeHeavy
                            )}
                            data-tn="pdp-spec-title"
                        >
                            {returnPolicySectionHeader}
                        </div>
                        <div
                            className={classNames(
                                dibsCss.sassyFontBody,
                                dibsCss.textNoir,
                                dibsCss.wFull
                            )}
                            data-tn="pdp-spec-return-policy"
                        >
                            <PdpSharedDetailReturnPolicy
                                item={item}
                                user={user}
                                sku={sku}
                                itemQuantity={itemQuantity}
                                userId={userId}
                                itemId={itemId || ''}
                                isClient={afterDisplay}
                            />
                        </div>
                    </div>
                </div>
                <PdpSharedBuyerProtectionBox />
            </div>
        );
    }

    return (
        <div
            className={classNames(styles.wrapper, styles.shippingModule, {
                [styles.noMargin]: isCondensedTab,
            })}
            data-tn="pdp-details-shipping"
        >
            <HeadingLevel>
                {Heading => (
                    <>
                        <GridRow>
                            <GridCol desktop={4} mobile={6}>
                                {!isCondensedTab && (
                                    <Heading className={styles.header}>
                                        {deliveryReturnsTitle}
                                    </Heading>
                                )}
                                <ul className={styles.shippingReturnsRow}>
                                    {/* Shipping */}
                                    <PdpRespDetail
                                        title={deliveryTitle}
                                        dataTn="shipping"
                                        printHide
                                    >
                                        <PdpSharedDetailShipping
                                            currency={currency}
                                            isClient={!!afterDisplay}
                                            item={item}
                                            user={user}
                                            preLinkQuoteDetails={preLinkQuoteDetails}
                                            displayInline
                                            itemQuantity={itemQuantity}
                                        />
                                    </PdpRespDetail>
                                    {/* Return Policy */}
                                    <PdpRespDetail
                                        title={returnPolicySectionHeader}
                                        dataTn="return-policy"
                                    >
                                        <PdpSharedDetailReturnPolicy
                                            item={item}
                                            user={user}
                                            sku={sku}
                                            itemQuantity={itemQuantity}
                                            userId={userId}
                                            itemId={itemId || ''}
                                            isClient={afterDisplay}
                                        />
                                    </PdpRespDetail>
                                </ul>
                            </GridCol>
                            <GridCol desktop={4} mobile={12}>
                                <div
                                    className={classNames(
                                        styles.printHide,
                                        styles.buyerProtectionRow,
                                        {
                                            [styles.marginTop]: !isCondensedTab,
                                        }
                                    )}
                                >
                                    <PdpSharedBuyerProtectionBox />
                                </div>
                            </GridCol>
                        </GridRow>
                    </>
                )}
            </HeadingLevel>
        </div>
    );
};
